import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { usePopup } from '../../contexts/PopupContext';
import { supabase } from '../../lib/supabase';
import { getFriends } from '../../utils/friendsService';
import {
  applyDiscoverEventFilters,
  defaultMapDiscoverFilters,
  dismissDiscoverFilterChip,
  mapDiscoverFiltersActive,
} from '../../utils/discoverEventFilters';
import { buildMapActiveFilterChips } from '../../utils/mapActiveFilterChips';
import EventCard from '../discover/EventCard';
import MapActiveFilterChips from '../discover/map/MapActiveFilterChips';
import Loading from '../layout/Loading';
import EventEmptyStateIcon from './EventEmptyStateIcon';
import './UpcomingView.css';

function UpcomingView({ refreshKey = 0, isActive = true }) {
  const { t } = useTranslation(['events', 'discover']);
  const { openPopup } = usePopup();
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(defaultMapDiscoverFilters());
  const [currentUserId, setCurrentUserId] = useState(null);
  const [friendIdSet, setFriendIdSet] = useState(new Set());
  const channelRef = useRef(null);
  const refreshKeyRef = useRef(refreshKey);
  refreshKeyRef.current = refreshKey;
  /** Ostatni refreshKey, dla którego mamy poprawnie wczytaną listę (unik ponownego „Ładowanie” przy zmianie zakładki). */
  const loadedRefreshKeyRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (cancelled) return;
      setCurrentUserId(user?.id ?? null);
      if (!user?.id) {
        setFriendIdSet(new Set());
        return;
      }
      const fr = await getFriends(user.id);
      if (cancelled) return;
      if (fr.success && Array.isArray(fr.data)) {
        setFriendIdSet(new Set(fr.data.map((f) => f.id)));
      } else {
        setFriendIdSet(new Set());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const applyFiltersToEvents = useCallback((eventsList) => {
    const filtered = applyDiscoverEventFilters(eventsList, filters, {
      friendIds: friendIdSet,
      currentUserId,
    });
    setFilteredEvents(filtered);
  }, [filters, friendIdSet, currentUserId]);

  const loadUpcomingEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      const today = new Date().toISOString().split('T')[0];

      // Load events where user is host or participant
      // First, get events where user is host
      let hostedQuery = supabase
        .from('events')
        .select('*')
        .eq('host_id', user.id)
        .gte('event_date', today)
        .order('event_date', { ascending: true })
        .order('start_time', { ascending: true });

      // Try to add is_cancelled filter, but don't fail if column doesn't exist
      try {
        hostedQuery = hostedQuery.eq('is_cancelled', false);
      } catch (err) {
        // Column doesn't exist yet, continue without filter
      }

      let { data: hostedEvents, error: hostedError } = await hostedQuery;

      // If error is about missing column, retry without filter
      if (hostedError && hostedError.message && hostedError.message.includes('is_cancelled')) {
        const retryResult = await supabase
          .from('events')
          .select('*')
          .eq('host_id', user.id)
          .gte('event_date', today)
          .order('event_date', { ascending: true })
          .order('start_time', { ascending: true });
        hostedEvents = retryResult.data;
        hostedError = retryResult.error;
      }

      if (hostedError) throw hostedError;

      // Filter out cancelled hosted events if is_cancelled field exists
      const filteredHostedEvents = (hostedEvents || []).filter(event => event.is_cancelled !== true);

      // Get events where user is participant with ACTIVE status only
      // Exclude kicked or withdrawn participants
      let participantQuery = supabase
        .from('event_participants')
        .select('event_id, events!inner (*)')
        .eq('user_id', user.id)
        .eq('status', 'active');

      // Try to add is_cancelled filter, but don't fail if column doesn't exist
      try {
        participantQuery = participantQuery.eq('events.is_cancelled', false);
      } catch (err) {
        // Column doesn't exist yet, continue without filter
      }

      let { data: participantEvents, error: participantError } = await participantQuery;

      // If error is about missing column, retry without filter
      if (participantError && participantError.message && participantError.message.includes('is_cancelled')) {
        const retryResult = await supabase
          .from('event_participants')
          .select('event_id, events!inner (*)')
          .eq('user_id', user.id)
          .eq('status', 'active');
        participantEvents = retryResult.data;
        participantError = retryResult.error;
      }

      if (participantError) throw participantError;

      // Combine and deduplicate events
      const allEvents = [];
      const eventIds = new Set();

      // Add hosted events
      if (filteredHostedEvents) {
        filteredHostedEvents.forEach(event => {
          if (!eventIds.has(event.id)) {
            eventIds.add(event.id);
            allEvents.push(event);
          }
        });
      }

      // Add participant events (only active participants, not cancelled)
      if (participantEvents) {
        participantEvents.forEach(({ events: event }) => {
          // Filter out cancelled events if is_cancelled field exists
          if (event && event.is_cancelled !== true && !eventIds.has(event.id) && event.event_date >= today) {
            eventIds.add(event.id);
            allEvents.push(event);
          }
        });
      }

      // Sort by date and time
      allEvents.sort((a, b) => {
        const dateA = new Date(`${a.event_date}T${a.start_time}`);
        const dateB = new Date(`${b.event_date}T${b.start_time}`);
        return dateA - dateB;
      });

      setEvents(allEvents);
      loadedRefreshKeyRef.current = refreshKeyRef.current;
    } catch (err) {
      console.error('Error loading upcoming events:', err);
      setError(err.message || t('events.upcoming.error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Apply filters when events or filters change
  useEffect(() => {
    if (events.length > 0) {
      applyFiltersToEvents(events);
    } else {
      setFilteredEvents([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, filters]); // Apply filters whenever events or filters change

  useEffect(() => {
    if (!isActive) {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      return undefined;
    }

    if (loadedRefreshKeyRef.current !== refreshKey) {
      loadUpcomingEvents();
    }

    let cancelled = false;

    const setupRealtimeSubscription = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || cancelled) return;

      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }

      const channel = supabase
        .channel(`upcoming-participants-${user.id}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'event_participants',
            filter: `user_id=eq.${user.id}`
          },
          async (payload) => {
            console.log('Participant change detected in UpcomingView:', payload);
            loadUpcomingEvents();
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'events',
            filter: `host_id=eq.${user.id}`
          },
          async (payload) => {
            console.log('Event change detected in UpcomingView (user is host):', payload);
            loadUpcomingEvents();
          }
        )
        .subscribe();

      if (!cancelled) {
        channelRef.current = channel;
      } else {
        supabase.removeChannel(channel);
      }
    };

    setupRealtimeSubscription();

    return () => {
      cancelled = true;
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [isActive, refreshKey, loadUpcomingEvents]);


  if (loading) {
    return (
      <div className="upcoming-view">
        <Loading 
          className="upcoming-view-loading" 
          size="medium"
          message={t('events.upcoming.loading')}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="upcoming-view">
        <div className="upcoming-view-error">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const handleOpenFilters = () => {
    openPopup('event-filters', {
      initialFilters: filters,
      onApply: (newFilters) => {
        setFilters(newFilters);
      }
    }, 0);
  };

  const hasActiveFilters = () => {
    return mapDiscoverFiltersActive(filters);
  };

  const filtersActive = hasActiveFilters();
  const displayEvents = filtersActive ? filteredEvents : events;
  const displayCount = filtersActive ? filteredEvents.length : events.length;
  const activeFilterChips = buildMapActiveFilterChips(
    { searchCenter: null, searchAddress: '', searchRange: '', mapDiscoverFilters: filters },
    t
  );

  const handleDismissFilterChip = (chipId) => {
    setFilters((prev) => dismissDiscoverFilterChip(prev, chipId));
  };

  if (events.length === 0) {
    return (
      <div className="upcoming-view">
        <div className="upcoming-view-empty upcoming-view-empty-card">
          <div className="upcoming-view-empty-icon" aria-hidden="true">
            <EventEmptyStateIcon variant="upcoming" />
          </div>
          <h2>{t('events.upcoming.title')}</h2>
          <p>{t('events.upcoming.description')}</p>
          <span className="upcoming-view-empty-tip">
            {t('events.upcoming.emptyTip', 'Dołącz do wydarzeń lub utwórz własne - pokażą się tutaj automatycznie.')}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="upcoming-view">
      <div className="upcoming-view-header">
        <div className="upcoming-view-header-left">
          <h2>{t('events.upcoming.title')}</h2>
          <p className="upcoming-view-count">
            {displayCount} {t('events.upcoming.eventsCount')}
            {hasActiveFilters() && events.length !== displayCount && (
              <span className="upcoming-view-filters-active">
                {' '}({t('events.filters.filtered', 'przefiltrowane')} z {events.length})
              </span>
            )}
          </p>
          {hasActiveFilters() && (
            <span className="upcoming-view-filters-active-badge">
              {t('events.filters.active', 'Filtry aktywne')}
            </span>
          )}
        </div>
        <button
          className="filter-button"
          onClick={handleOpenFilters}
          title={t('events.filters.filter', 'Filtruj')}
        >
          <svg className="filter-button-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
            <path d="M4 6h16M8 12h8M10 18h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="6" cy="6" r="1.75" fill="currentColor" />
            <circle cx="16" cy="12" r="1.75" fill="currentColor" />
            <circle cx="14" cy="18" r="1.75" fill="currentColor" />
          </svg>
          {t('events.filters.filter', 'Filtruj')}
          {hasActiveFilters() && <span className="filter-badge">•</span>}
        </button>
      </div>
      {filtersActive && (
        <MapActiveFilterChips chips={activeFilterChips} onDismiss={handleDismissFilterChip} />
      )}
      <div className="upcoming-view-events">
        {displayEvents.length > 0 ? (
          displayEvents.map((event) => (
            <EventCard key={event.id} event={event} />
          ))
        ) : (
          <div className="upcoming-view-empty">
            <p>{t('events.filters.noResults', 'Brak wydarzeń spełniających wybrane kryteria')}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default UpcomingView;
